import { type Carer, type Home, HomeSource, UserRole } from '@lmc/ts-common'
import { InputRadioGroup, Popover } from '@lmc/ui-kit'
import {
  PREFERRED_TERM_CARE_OPTIONS,
  PREFERRED_TERM_RES_OPTIONS
} from 'common/constants'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import Operations from 'operations'

import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  OnboardingButton,
  OnboardingButtonWrapper,
  OnboardingStepIndicator
} from './OnboardingButton'
import { OnboardingHeading } from './OnboardingHeading'
import { OnboardingLayout } from './OnboardingLayout'
import { OnboardingText } from './OnboardingText'

export type Props = { next: () => void }

export const StepPreferences: FC<Props> = () => {
  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const { t: tGlobal } = useTranslation()

  const dispatch = useDispatch()

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user
  )

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const hasPin = (user as unknown as { hasPin: boolean })?.hasPin
  const hasPro = home?.features?.pro?.enabled
  const isSelfServe = home?.source === HomeSource.selfServe
  const hasAdminCarers =
    carers?.filter((carer) => carer.role === UserRole.careHomeAdmin)?.length > 1

  // Calculate total steps based on logic in SetupScreen
  const getTotalSteps = () => {
    // If hasPin is true, user will be redirected home after step 1
    if (hasPin) {
      return 1
    }
    // If self-serve without Pro, won't reach step 2
    if (isSelfServe && !hasPro) {
      return 1
    }
    // If hasAdminCarers, step 2 (invite) is skipped, so only 2 steps visible
    // User sees: Step 1 (Preferences) → Step 3 (PIN)
    if (hasAdminCarers) {
      return 2
    }
    // All steps shown: Step 1 (Preferences) → Step 2 (Invite) → Step 3 (PIN)
    return 3
  }

  const totalSteps = getTotalSteps()

  const updatePreferences = () => {
    dispatch(
      Operations.saveHomeDetails({
        id: home?.id,
        preferredPeopleNominative,
        preferredCarePlansNominative,
        preferencesCustomised: true
      })
    )
  }

  const [preferredCarePlansNominative, setPreferredCarePlansNominative] =
    useState<string>('')

  const [preferredPeopleNominative, setPreferredPeopleNominative] =
    useState<string>('')

  const canContinue =
    preferredCarePlansNominative !== '' && preferredPeopleNominative !== ''

  return (
    <OnboardingLayout>
      <OnboardingHeading level={1}>
        {t(selfServeConstants.SET_YOUR_PREFFERED)}
      </OnboardingHeading>
      <OnboardingText>{t(selfServeConstants.BY_CHANGING)}</OnboardingText>
      <MaxWidth>
        <InputRadioGroupStyled
          onChange={(e) => {
            setPreferredPeopleNominative(e)
          }}
          value={preferredPeopleNominative}
          label={tGlobal('general.options.peopleOptions.label')}
          options={PREFERRED_TERM_RES_OPTIONS}
        />
        <InputRadioGroupStyled
          onChange={(e) => setPreferredCarePlansNominative(e)}
          value={preferredCarePlansNominative}
          label={tGlobal('general.options.planOptions.label')}
          options={PREFERRED_TERM_CARE_OPTIONS}
        />
        <HelperText>{t(selfServeConstants.MAKE_SELECTION)}</HelperText>
      </MaxWidth>
      <OnboardingButtonWrapper>
        {canContinue ? (
          <OnboardingButton
            variant="default"
            size="large"
            onClick={updatePreferences}
          >
            {t(selfServeConstants.NEXT)}
          </OnboardingButton>
        ) : (
          <Popover content={t(selfServeConstants.MAKE_SELECTION)}>
            <StyledDisabledButton variant="default" size="large">
              {t(selfServeConstants.NEXT)}
            </StyledDisabledButton>
          </Popover>
        )}
        {totalSteps > 1 && (
          <OnboardingStepIndicator>
            {t(selfServeConstants.STEP_X_OF_X, { from: 1, to: totalSteps })}
          </OnboardingStepIndicator>
        )}
      </OnboardingButtonWrapper>
    </OnboardingLayout>
  )
}

const MaxWidth = styled.div`
  width: 600;
`

const HelperText = styled(OnboardingText)`
  margin-top: 10px;
  font-size: 16px;
  opacity: 0.8;
`

const StyledDisabledButton = styled(OnboardingButton)`
  opacity: 0.5;
`

const InputRadioGroupStyled = styled(InputRadioGroup)`
  max-width: 900px;
  .ant-radio-group {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .ant-radio-wrapper > span {
    font-size: 16px !important;
  }

  .ant-radio-wrapper {
    display: flex;
    flex-direction: row-reverse;
    border-radius: 9px;
    border: 1px solid ${({ theme }) => theme.color.neutral.color5};
    .ant-radio-inner {
        border-color: ${({ theme }) => theme.color.neutral.color5} !important;
        
    }

    &.ant-radio-wrapper-checked {
      border: 1px solid ${({ theme }) => theme.color.neutral.color5} !important;

      .ant-radio-inner {
        border-color: ${({ theme }) => theme.palette.lmcPink} !important;
        background-color: ${({ theme }) => theme.palette.lmcPink} !important;
      }
    }

   

    &:hover {
      border: 1px solid ${({ theme }) => theme.color.neutral.color5} !important;
    }
  }

  .ant_not_full_width {
    min-width: unset;
  }

  .ant-form-item-label > label {
    color: ${({ theme }) => theme.palette.darkpurple} !important;
    font-size: 20px;
    font-weight: 700;
    margin-top: 15px;
    margin-bottom: 15px;
  }
`
